/**
 * Gift-feature shared constants (frontend).
 *
 * The platform fee bps is read from the on-chain `PlatformConfig` account
 * via `useGiftFeeBps()` — never defaulted on the client.
 */

import BN from 'bn.js';
import { D6_SCALE } from '@/config/solana';

/** Maximum visible-character byte budget for a gift message (FR-008). */
export const GIFT_MESSAGE_MAX_BYTES = 140 as const;

/** Minimum USDC amount (D6) per gift. Mirrors `MIN_GIFT_AMOUNT_USDC_D6`. */
export const MIN_GIFT_AMOUNT_USDC_D6: BN = new BN('100000'); // 0.10 USDC

/** Minimum platform-fee floor in USDC base units (D6). */
export const GIFT_FEE_FLOOR_USDC_D6: BN = new BN('10000'); // 0.01 USDC

/**
 * USDC-D6 → display USDC (as a `number` for client-side validation only).
 * Never use this value in money math — it's lossy above 2^53.
 */
export function d6ToDisplayUsdc(d6: BN | string | number): number {
  const bn = BN.isBN(d6) ? d6 : new BN(String(d6));
  // BN division loses precision below D6; for display-validation we accept it.
  const usdcWhole = bn.div(new BN(D6_SCALE)).toNumber();
  const remainder = bn.mod(new BN(D6_SCALE)).toNumber();
  return usdcWhole + remainder / D6_SCALE;
}

/**
 * Compute the platform fee for a given USDC gift amount.
 *
 * Mirrors the on-chain calculation in
 * `contracts/programs/morfi-opinion/src/instructions/creator/send_usdc_to_handle.rs`:
 *
 *   raw_fee = amount * fee_bps / 10_000
 *   fee     = max(raw_fee, fee_floor)
 *
 * Returns the fee as USDC base units (D6).
 */
export function computeGiftFeeD6(amountD6: BN, feeBps: number): BN {
  if (feeBps <= 0) return new BN(0);
  const rawFee = amountD6.mul(new BN(feeBps)).div(new BN(10_000));
  return BN.max(rawFee, GIFT_FEE_FLOOR_USDC_D6);
}

/**
 * Total USDC the sender's wallet is debited when sending a USDC gift.
 *
 * The fee is taken **on top** of the gift amount — the recipient receives the
 * full `amountD6`, and the sender pays `amount + fee`. This matches the
 * outcome-token gift flow (where the user transfers X tokens AND pays the fee
 * in USDC separately) and the on-chain `send_usdc_to_handle` instruction
 * post-2026-04 rewrite.
 */
export function computeTotalDeductedD6(amountD6: BN, feeBps: number): BN {
  return amountD6.add(computeGiftFeeD6(amountD6, feeBps));
}

/**
 * Solve for the largest gift amount whose `amount + fee` fits inside the given
 * wallet balance, given a fee scheme of `max(amount * feeBps / 10_000, floor)`.
 *
 * Algebra (no float — all BigInt):
 *
 *   We want max `amount` s.t. `amount + fee(amount) ≤ balance`.
 *   Above the fee-floor break-even, `fee = amount * feeBps / 10_000`, so:
 *     amount * (1 + feeBps/10_000) ≤ balance
 *     amount ≤ balance * 10_000 / (10_000 + feeBps)
 *   Take the floor: `amount = balance * 10_000 / (10_000 + feeBps)` (integer div).
 *
 *   Below the break-even, `fee = floor`, so:
 *     amount ≤ balance - floor
 *
 *   The final answer is the smaller of the two when the proportional fee bites
 *   the floor — but in practice we just compute the proportional candidate
 *   first, then back off by 1 base unit while `amount + fee > balance`. The
 *   integer rounding plus the fee-floor rule may push us 1 unit over; the loop
 *   converges in at most a few iterations.
 *
 * Returns the maximum giftable amount (D6), or 0 if the balance can't even
 * cover the fee floor.
 */
export function computeMaxGiftAmountD6(balanceD6: BN, feeBps: number): BN {
  const ZERO = new BN(0);
  if (balanceD6.lte(ZERO)) return ZERO;
  if (feeBps <= 0) return balanceD6;

  // Proportional candidate.
  const denom = new BN(10_000 + feeBps);
  let candidate = balanceD6.mul(new BN(10_000)).div(denom);

  // Back off by 1 base unit while amount + fee overshoots the balance. The
  // fee-floor clamp can push us over; this loop converges quickly.
  while (candidate.gt(ZERO) && computeTotalDeductedD6(candidate, feeBps).gt(balanceD6)) {
    candidate = candidate.sub(new BN(1));
  }

  if (candidate.lte(ZERO)) return ZERO;
  return candidate;
}
